import { Component, View, OnInit } from "angular2/core";
import { CORE_DIRECTIVES, DatePipe } from "angular2/common";
import { ROUTER_DIRECTIVES, Router } from "angular2/router";
import { Response } from "angular2/http";
import { Observable } from "rxjs/Observable";

import { Attendee } from "./attendee.model";
import { AttendeeService } from "./attendee.service";
import { Header } from "../layout/header/header";
import { Sidebar } from "../layout/sidebar/sidebar";
import { Event } from "../event/event.model";
import { EventService, IBookingData } from "../event/event.service";
import { Company } from "../company/company.model";
import { CompanyService } from "../company/company.service";
import { Alert } from "../alert/alert";
import {
  AlertService,
  AlertMessage,
  MessageType,
} from "../alert/alert.service";
import { Nl2pPipe } from "./../strings/nltop-pipe";
import { API_HOST } from "../config/config";

const USER_KEY = "attendee_data_v2";

let template = require("./attendee-register.html");

@Component({
  selector: "attendee-register",
})
@View({
  template: template,
  pipes: [DatePipe, Nl2pPipe],
  directives: [CORE_DIRECTIVES, Alert],
})
export class AttendeeRegister implements OnInit {
  userLoaded: boolean = false;
  step: number = 0;
  numSteps: number = 3;
  attendee: Attendee;
  events: Event[] = [];
  companies: Company[] = [];
  visibleAttendees: Attendee[] = [];
  eventDetail: Event;
  bookingData: BookingData = null;
  showConfirm = false;
  showAttendees = false;
  errors = {};
  apiHost: string = API_HOST;

  constructor(
    public router: Router,
    public eventService: EventService,
    public companyService: CompanyService,
    public attendeeService: AttendeeService,
    private alertService: AlertService,
  ) {}

  ngOnInit() {
    this.attendee = new Attendee();
    this.loadCompanies();
    this.loadUserData();
  }

  loadEvents() {
    this.eventService.listPublic().subscribe(
      (response) => {
        let events = [];
        for (let event of response.json()) {
          events.push(Event.fromJSON(event));
        }
        this.events = events;
      },
      (error) => {
        this.alert(
          "Error al recuperar el listado de eventos",
          MessageType.Danger,
        );
      },
    );
  }

  loadCompanies() {
    this.companyService.listPublic().subscribe(
      (response) => {
        let companies = [];
        for (let company of response.json()) {
          companies.push(new Company().fromJson(company));
        }
        function sortFunc(a: any, b: any): number {
          let compA = a["name"].toLowerCase();
          let compB = b["name"].toLowerCase();
          if (compA !== compB) {
            return compA < compB ? -1 : 1;
          }
          return 0;
        }
        companies.sort(sortFunc);
        this.companies = companies;
      },
      (error) => {
        this.alert(
          "Error al recuperar el listado de empresas",
          MessageType.Danger,
        );
      },
    );
  }

  next($event) {
    $event.preventDefault();
    if (this.step >= this.numSteps - 1) {
      return;
    }
    this.step++;
    switch (this.step) {
      case 1:
        this.storeUserData();
        break;

      case 2:
        this.loadEvents();
        break;
    }
  }

  loadUserData(): void {
    let json = localStorage.getItem(USER_KEY);
    if (!json) {
      return;
    }
    let attendee = Attendee.fromObject(JSON.parse(json));
    if (attendee.email) {
      this.userLoaded = true;
      this.attendee = attendee;
    }
  }

  storeUserData(): void {
    localStorage.setItem(USER_KEY, JSON.stringify(this.attendee));
  }

  clearUserData($event): void {
    $event.preventDefault();
    localStorage.removeItem(USER_KEY);
    this.userLoaded = false;
    this.attendee = new Attendee();
  }

  previous($event) {
    $event.preventDefault();
    if (this.step <= 0) {
      return;
    }
    this.step--;
  }

  showEventDetail($event, event: Event): void {
    $event.preventDefault();
    this.eventDetail = event;
  }

  hideEventDetail($event): void {
    $event.preventDefault();
    this.eventDetail = null;
  }

  showAttendeeList($event, event: Event): void {
    $event.preventDefault();
    this.showAttendees = true;

    this.eventService.getAttendeesPub(event).subscribe((attendees) => {
      this.visibleAttendees = attendees;
    });
  }

  hideAttendeeList($event): void {
    $event.preventDefault();
    this.visibleAttendees = [];
    this.showAttendees = false;
  }

  formatAttendee(attendee: Attendee): string {
    return (
      attendee.firstName +
      " " +
      attendee.lastName +
      " ( " +
      this.companyName(attendee.companyId) +
      " )"
    );
  }

  showBookingForm($event, event: Event, isRequest?: boolean): void {
    $event.preventDefault();

    let bookingData = new BookingData();
    bookingData.event = event;
    bookingData.attendee = this.attendee;
    if (isRequest) {
      bookingData.isRequest = true;
    } else {
      bookingData.vetoedCompanyIds = this.selectedCompanyIds();
    }
    this.bookingData = bookingData;
  }

  cancelBooking($event): void {
    $event.preventDefault();
    this.bookingData = null;
  }

  clearBooking($event): void {
    $event.preventDefault();
    this.showConfirm = false;
    this.bookingData = null;
  }

  book($event): void {
    $event.preventDefault();

    // Validate all fields in one pass so the user sees all missing requirements
    let hasErrors = false;

    if (!this.bookingData || !this.bookingData.comments) {
      this.errors["booking.comments"] = true;
      hasErrors = true;
    } else if (this.bookingData.comments.length < 10) {
      this.errors["booking.comments"] = true;
      hasErrors = true;
    } else {
      delete this.errors["booking.comments"];
    }

    if (!this.bookingData || !this.bookingData.acceptTerms) {
      this.errors["booking.acceptTerms"] = true;
      hasErrors = true;
    } else {
      delete this.errors["booking.acceptTerms"];
    }

    if (!this.bookingData || !this.bookingData.acceptCompetitionTerms) {
      this.errors["booking.acceptCompetitionTerms"] = true;
      hasErrors = true;
    } else {
      delete this.errors["booking.acceptCompetitionTerms"];
    }

    if (hasErrors) {
      return;
    }

    this.getRegisteredAttendee().subscribe((attendee) => {
      this.bookingData.attendee = attendee;
      this.bookEvent(this.bookingData);
    });
  }

  isDisabledNext(): boolean {
    if (this.step === 0) {
      return !this.validAttendee();
    }
    return false;
  }

  validAttendee(): boolean {
    if (
      this.attendee.firstName.length >= 2 &&
      this.attendee.lastName.length >= 2 &&
      this.validateEmail(this.attendee.email) &&
      this.attendee.phone1.length >= 9 &&
      this.attendee.companyId.length === 24
    ) {
      return true;
    }
    return false;
  }

  companyName(companyId: string): string {
    for (let company of this.companies) {
      if (company.id === companyId) {
        return company.name;
      }
    }
    return "";
  }

  validateEmail(email: string): boolean {
    var re = /@.*?\./;
    return re.test(email);
  }

  alert(text: string, type: MessageType) {
    this.alertService.push({ text, type });
  }

  getStatus(event: Event): string {
    if (event.full) {
      return "Completo";
    }
    if (!this.isCompatible(event, this.attendee, this.selectedCompanyIds())) {
      return "No disponible. Vetado por uno o más competidores";
    }
    return "Disponible";
  }

  isBookable(event: Event): boolean {
    return (
      this.isCompatible(event, this.attendee, this.selectedCompanyIds()) &&
      !event.full
    );
  }

  dimissBookConfirmation(): void {
    this.showConfirm = null;
    this.bookingData = null;
  }

  private isCompatible(
    event: Event,
    attendee: Attendee,
    vetoedCompanies: string[],
  ): boolean {
    if (!event.companyAllowed(attendee.companyId)) {
      return false;
    }
    if (event.hasCompanies(vetoedCompanies)) {
      return false;
    }
    return true;
  }

  private selectedCompanies(): Company[] {
    return this.companies.filter((company) => {
      return company.selected;
    });
  }

  private selectedCompanyIds(): string[] {
    let companies = this.selectedCompanies();
    return companies.map((company) => {
      return company.id;
    });
  }

  private getRegisteredAttendee(): Observable<Attendee> {
    if (this.attendee.id) {
      return Observable.of(this.attendee);
    }
    return this.attendeeService.register(this.attendee).map((response) => {
      let attendee = Attendee.fromObject(response.json());
      this.attendee = attendee;
      this.storeUserData();
      return attendee;
    });
  }

  private bookEvent(data: BookingData): void {
    if (data.isRequest) {
      return this.requestEvent(data);
    }
    this.eventService.bookEvent(data.forServer()).subscribe(
      (response) => {
        this.showConfirm = true;
      },
      (error) => {
        this.alert("Error al crear la inscripción", MessageType.Danger);
      },
    );
  }

  private requestEvent(data: BookingData): void {
    this.eventService.requestEvent(data.forServer()).subscribe(
      (response) => {
        this.showConfirm = true;
      },
      (error) => {
        this.alert("Error al crear la petición", MessageType.Danger);
      },
    );
  }
}

class BookingData {
  event: Event = null;
  attendee: Attendee = null;
  asLeader: boolean = false;
  vetoedCompanyIds: string[] = [];
  comments: string = "";
  acceptTerms: boolean = false;
  acceptCompetitionTerms: boolean = false;
  isRequest: boolean = false;

  forServer(): IBookingData {
    let data = {
      eventId: this.event.id,
      attendeeId: this.attendee.id,
      companyId: this.attendee.companyId,
      comments: this.comments,
    };
    if (!this.isRequest) {
      console.log("not a request, assigning...");
      data = Object.assign(data, {
        vetoedCompanies: this.vetoedCompanyIds,
        asLeader: this.asLeader,
      });
    }
    console.log("booking data for request", data);
    return data;
  }
}



/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./src/attendee/attendee-register.ts
 **/